import type {
  ClusterHint,
  Defect,
  DefectComment,
  DefectListEntry,
  DefectPatch,
  DefectStatus,
} from '@testflow/shared';
import { request } from './api.js';
import type { DefectConfig } from './project-config-api.js';

// Review-Fix B6: `DefectConfig` gab es zweimal für denselben Endpunkt (/defect-config) — hier ohne,
// in project-config-api.ts mit `usage_count`. Gleicher Name, gleiche Quelle, divergente Form. Es
// gewinnt die Variante, die der Server tatsächlich liefert; sie wird hier nur re-exportiert, damit
// bestehende Importe (`from './defects-api.js'`) unverändert funktionieren.
export type { DefectConfig };

// #39: manuelles Anlegen. Nur der Titel ist Pflicht; ohne test_case_id entsteht ein Defect
// ganz ohne Lauf- und Testfall-Bezug (Fund im Workshop, im Vorbeigehen).
export interface DefectCreateBody {
  title: string;
  description?: string | null;
  severity_id?: string | null;
  error_type_id?: string | null;
  test_case_id?: string | null;
  // #57: Herkunfts-Plan, vom Anlege-Dialog vorbelegt.
  test_plan_id?: string | null;
}

export interface TransitionResult extends Defect {
  retest_cascaded: boolean;
}

// #105: „blockiert außerdem" — die Anzeigeform (GET), mit Titeln aus dem gejointen Testfall.
// Getrennt von DefectAffectedLink (POST-Antwort): die n:m-Tabelle selbst trägt keine eigenen
// Anzeigefelder, nur defect_id/test_case_id/created_at (s. apps/api/src/db/schema.ts).
export interface DefectAffectedEntry {
  test_case_id: string;
  human_id: string | null;
  title_de: string | null;
  title_en: string | null;
}

export interface DefectAffectedLink {
  defect_id: string;
  test_case_id: string;
  created_at: string;
}

const base = (p: string) => `/v1/projects/${p}`;

export const defectsApi = {
  config: (p: string) => request<DefectConfig>(`${base(p)}/defect-config`),
  // #57: test_plan_id kennt drei Zustaende — weggelassen (alle), eine UUID, oder 'none'
  // (nur Defects ohne Plan). Ein leerer Wert waere von „weggelassen" nicht unterscheidbar.
  list: (
    p: string,
    filters?: { severity_id?: string; assignee_id?: string; test_plan_id?: string },
  ) => {
    const q = new URLSearchParams();
    if (filters?.severity_id) q.set('severity_id', filters.severity_id);
    if (filters?.assignee_id) q.set('assignee_id', filters.assignee_id);
    if (filters?.test_plan_id) q.set('test_plan_id', filters.test_plan_id);
    const qs = q.toString();
    return request<DefectListEntry[]>(`${base(p)}/defects${qs ? `?${qs}` : ''}`);
  },
  get: (p: string, id: string) => request<Defect>(`${base(p)}/defects/${id}`),
  patch: (p: string, id: string, body: DefectPatch) =>
    request<Defect>(`${base(p)}/defects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // #87: `comment` bleibt optional — nur der Close-Guard (Router) verlangt ihn, bei jedem anderen
  // Übergang ignoriert der Server das Feld. Wird NUR gesendet, wenn gesetzt (kein `comment: undefined`
  // im Body): so bleiben bestehende 3-Argumente-Aufrufe unverändert testbar.
  transition: (p: string, id: string, to: DefectStatus, comment?: string) =>
    request<TransitionResult>(`${base(p)}/defects/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify(comment ? { to, comment } : { to }),
    }),
  // #105: „blockiert außerdem" — Liste, Anlegen, Entfernen.
  affected: (p: string, id: string) =>
    request<DefectAffectedEntry[]>(`${base(p)}/defects/${id}/affected`),
  addAffected: (p: string, id: string, testCaseId: string) =>
    request<DefectAffectedLink>(`${base(p)}/defects/${id}/affected`, {
      method: 'POST',
      body: JSON.stringify({ test_case_id: testCaseId }),
    }),
  removeAffected: (p: string, id: string, testCaseId: string) =>
    request<void>(`${base(p)}/defects/${id}/affected/${testCaseId}`, { method: 'DELETE' }),
  clusterHints: (p: string, id: string) =>
    request<ClusterHint[]>(`${base(p)}/defects/${id}/cluster-hints`),
  // #27: Verlauf — Kommentare und Status-Wechsel in einer chronologischen Liste.
  comments: (p: string, id: string) =>
    request<DefectComment[]>(`${base(p)}/defects/${id}/comments`),
  addComment: (p: string, id: string, body: string) =>
    request<DefectComment>(`${base(p)}/defects/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  create: (p: string, body: DefectCreateBody) =>
    request<Defect>(`${base(p)}/defects`, { method: 'POST', body: JSON.stringify(body) }),
  // #91: Sammel-Severity/-Bearbeiter über die Board-Tabellen-Aktionsleiste — EIN Request statt N
  // Einzel-PATCHes. Server-Vertrag (Task 11, GARANTIERT): `updated + skipped.length` ===
  // deduplizierte Eingabe, Reason-Code je Skip ist immer `not_found`. „Severity entfernen" gibt es
  // im Bulk bewusst NICHT (Schema) — `severity_id` ist nur setzbar, nie löschbar.
  bulkUpdate: (
    p: string,
    body: { defect_ids: string[]; patch: { severity_id?: string; assignee_id?: string | null } },
  ) =>
    request<{ updated: number; skipped: { defect_id: string; reason: string }[] }>(
      `${base(p)}/defects:bulk-update`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};
